import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { KeyRound, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { logError } from '@/lib/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
};

const statusStyles = {
  active: 'bg-emerald-100 text-emerald-700',
  trialing: 'bg-blue-100 text-blue-700',
  inactive: 'bg-stone-100 text-stone-600',
  past_due: 'bg-red-100 text-red-700',
};

const StatusBadge = ({ status }) => (
  <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusStyles[status] || statusStyles.inactive}`}>
    {status}
  </span>
);

const SendResetLinkButton = ({ userId, userEmail }) => {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const send = async () => {
    if (sending || sent) return;
    if (!window.confirm(`Send password reset link to ${userEmail}?`)) return;
    setSending(true);
    try {
      await axios.post(`${API}/admin/users/${userId}/send-password-reset`);
      setSent(true);
      toast.success('Reset link sent.');
    } catch (err) {
      logError('admin send-password-reset failed:', err?.response?.status);
      toast.error(err?.response?.data?.detail || 'Could not send reset link.');
    } finally {
      setSending(false);
    }
  };

  return (
    <Button
      onClick={send}
      variant="outline"
      size="sm"
      disabled={sending || sent}
      className="text-xs rounded-full"
      data-testid={`send-reset-link-${userId}`}
    >
      {sending ? (
        <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Sending…</>
      ) : sent ? (
        <><KeyRound className="w-3 h-3 mr-1" /> Sent</>
      ) : (
        <><KeyRound className="w-3 h-3 mr-1" /> Reset link</>
      )}
    </Button>
  );
};

export const UsersView = ({ users }) => {
  return (
    <div className="space-y-6 sm:space-y-8">
      <h2
        className="text-2xl sm:text-3xl font-medium"
        style={{ fontFamily: 'Playfair Display, serif' }}
      >
        User Management ({users.length})
      </h2>

      <div className="bg-white/60 backdrop-blur-md border border-[#EAE4DC] shadow-lg rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px]">
            <thead>
              <tr className="border-b border-stone-200 bg-stone-50/50">
                <th className="text-left p-4 text-sm font-medium text-stone-600">Email</th>
                <th className="text-left p-4 text-sm font-medium text-stone-600">Name</th>
                <th className="text-left p-4 text-sm font-medium text-stone-600">Phase</th>
                <th className="text-left p-4 text-sm font-medium text-stone-600">Status</th>
                <th className="text-left p-4 text-sm font-medium text-stone-600">Joined</th>
                <th className="text-right p-4 text-sm font-medium text-stone-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr
                  key={user.id}
                  className="border-b border-stone-100 hover:bg-stone-50/50 transition-colors"
                >
                  <td className="p-4">
                    <span className="text-sm text-stone-800">{user.email}</span>
                  </td>
                  <td className="p-4">
                    <span className="text-sm text-stone-600">
                      {user.preferred_name || user.name || '-'}
                    </span>
                  </td>
                  <td className="p-4">
                    <span className="text-sm text-stone-600">
                      {user.current_phase ? `Phase ${user.current_phase}` : '-'}
                    </span>
                  </td>
                  <td className="p-4"><StatusBadge status={user.subscription_status} /></td>
                  <td className="p-4">
                    <span className="text-sm text-stone-500">{formatDate(user.created_at)}</span>
                  </td>
                  <td className="p-4 text-right">
                    <SendResetLinkButton userId={user.id} userEmail={user.email} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
